// Chakra imports
import { toastError, presentMutationError } from "../../../../utils/showApiErrorToast";
import {
  Box,
  Flex,
  Input,
  Text,
  Select,
  SimpleGrid,
  VStack,
  Button,
  useColorModeValue,
  useDisclosure,
  Image,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
} from "@chakra-ui/react";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { useUser } from "@clerk/clerk-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { AiOutlineUpload, AiOutlineDelete } from "react-icons/ai";
import {
  getAttachments,
  uploadAttachment,
  deleteAttachment,
  getAttachmentDataURL,
} from "../../../../services/attachmentService";
import { Attachment } from "../../../../types/attachment";
// Custom components — match TradeDetailsCard shell
import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
// Types
import {
  TradeFormData,
  TradeValidationErrors,
  TradeType,
  TradeStatus,
} from "../../../../types/trade";
// Utils
import { calculateTradeValues } from "../../../../utils/tradeCalculations";
import { validateTradeForm } from "../../../../utils/tradeValidation";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";

interface TradeFormProps {
  /** Persisted trade UUID when editing, or a `pending-*` id for new trades (localStorage attachment bucket). */
  attachmentStorageId: string;
  initialData?: Partial<TradeFormData>;
  onSubmit?: (data: TradeFormData) => void;
  onCancel?: () => void;
  isLoading?: boolean;
}

const PRE_TRADE_MOODS = [
  { value: "calm", label: "Calm" },
  { value: "confident", label: "Confident" },
  { value: "excited", label: "Excited" },
  { value: "anxious", label: "Anxious" },
  { value: "uncertain", label: "Uncertain" },
  { value: "fearful", label: "Fearful" },
];

const DURING_TRADE_MOODS = [
  { value: "focused", label: "Focused" },
  { value: "calm", label: "Calm" },
  { value: "confident", label: "Confident" },
  { value: "nervous", label: "Nervous" },
  { value: "stressed", label: "Stressed" },
  { value: "impatient", label: "Impatient" },
  { value: "worried", label: "Worried" },
  { value: "relaxed", label: "Relaxed" },
];

const POST_TRADE_MOODS = [
  { value: "satisfied", label: "Satisfied" },
  { value: "proud", label: "Proud" },
  { value: "relieved", label: "Relieved" },
  { value: "frustrated", label: "Frustrated" },
  { value: "disappointed", label: "Disappointed" },
  { value: "regretful", label: "Regretful" },
];

/** Fields not shown in compact details layout — preserve on edit from initialData */
const EXTENDED_KEYS: (keyof TradeFormData)[] = [
  "strategy",
  "tags",
  "setupTags",
  "mistakeTags",
  "preTradeRationale",
  "preTradeTargets",
  "preTradeRiskPlan",
  "preTradeMarketConditions",
  "postTradeReview",
  "postTradeMistakes",
  "postTradeLessons",
  "postTradeSuccesses",
  "marketDirection",
  "sectorMovements",
  "volumeAnalysis",
  "marketNews",
  "sentimentIndicators",
  "preTradeConfidence",
  "followedPlan",
  "temptedEarlyExit",
  "addedToPosition",
  "emotionsInvolved",
  "plannedEntryPrice",
  "plannedExitPrice",
  "plannedStopLoss",
  "plannedTakeProfit",
  "plannedRisk",
  "plannedDuration",
  "mfe",
  "mae",
];

function mergeExtendedFromInitial(
  base: TradeFormData,
  initial?: Partial<TradeFormData>
): TradeFormData {
  if (!initial) return base;
  const out: TradeFormData = { ...base };
  for (const k of EXTENDED_KEYS) {
    const v = initial[k];
    if (v !== undefined) (out as unknown as Record<string, unknown>)[k as string] = v;
  }
  return out;
}

function calculateRiskRewardPreview(p: {
  entryPrice: number;
  type: TradeType;
  status: TradeStatus;
  exitPrice?: number;
}): number | null {
  const { entryPrice, type, status, exitPrice } = p;
  if (!entryPrice || entryPrice <= 0) return null;
  const isLong = type === "buy";
  const riskPercent = 0.015;
  const stopLoss = isLong ? entryPrice * (1 - riskPercent) : entryPrice * (1 + riskPercent);

  if (status === "closed" && exitPrice !== undefined && exitPrice > 0) {
    const risk = isLong ? entryPrice - stopLoss : stopLoss - entryPrice;
    const reward = isLong ? exitPrice - entryPrice : entryPrice - exitPrice;
    if (risk > 0) return Math.abs(reward / risk);
  }
  return 2;
}

const TradeForm = React.forwardRef<HTMLFormElement, TradeFormProps>(
  ({ attachmentStorageId, initialData, onSubmit, isLoading = false }, ref) => {
    const { formatMoney } = useDisplayCurrency();
    const { user } = useUser();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { isOpen: isImageModalOpen, onOpen: onImageModalOpen, onClose: onImageModalClose } = useDisclosure();
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [symbol, setSymbol] = useState(initialData?.symbol || "");
    const [type, setType] = useState<TradeType>(initialData?.type || "buy");
    const [status, setStatus] = useState<TradeStatus>(initialData?.status || "open");
    const [entryDate, setEntryDate] = useState(
      initialData?.entryDate || new Date().toISOString().slice(0, 16)
    );
    const [exitDate, setExitDate] = useState(initialData?.exitDate || "");
    const [entryPrice, setEntryPrice] = useState(initialData?.entryPrice?.toString() || "");
    const [entryQuantity, setEntryQuantity] = useState(initialData?.entryQuantity?.toString() || "");
    const [entryFees, setEntryFees] = useState(initialData?.entryFees?.toString() ?? "0");
    const [exitPrice, setExitPrice] = useState(initialData?.exitPrice?.toString() || "");
    const [exitQuantity, setExitQuantity] = useState(initialData?.exitQuantity?.toString() || "");
    const [exitFees, setExitFees] = useState(initialData?.exitFees?.toString() ?? "0");

    const [preTradeEmotion, setPreTradeEmotion] = useState(initialData?.preTradeEmotion || "");
    const [duringTradeEmotion, setDuringTradeEmotion] = useState(initialData?.duringTradeEmotion || "");
    const [postTradeEmotion, setPostTradeEmotion] = useState(initialData?.postTradeEmotion || "");
    const [journalContent, setJournalContent] = useState(initialData?.notes || "");

    const [errors, setErrors] = useState<TradeValidationErrors>({});

    const textColor = useColorModeValue("#575757", "white");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const inputBg = useColorModeValue("white", "gray.700");
    const dashedAreaBg = useColorModeValue("gray.50", "gray.700");
    const toolbarBg = useColorModeValue("gray.50", "gray.800");
    const modalBg = useColorModeValue("white", "#151517");

    const labelProps = {
      fontSize: "xs",
      color: "gray.400",
      mb: 1,
      fontFamily: "'IBM Plex Sans', sans-serif",
      fontWeight: "medium" as const,
      textTransform: "uppercase" as const,
    };

    const journalLabelProps = {
      fontSize: "xs",
      color: "gray.400",
      mb: 2,
      fontFamily: "'IBM Plex Sans', sans-serif",
      fontWeight: "medium" as const,
      textTransform: "uppercase" as const,
    };

    const inputFieldProps = {
      size: "sm" as const,
      isDisabled: isLoading,
      bg: inputBg,
      borderColor,
      borderRadius: "md",
      fontFamily: "'IBM Plex Sans', sans-serif",
      fontSize: "sm",
      color: textColor,
      _focus: { borderColor: "#38BBD1", boxShadow: "0 0 0 1px #38BBD1" },
    };

    const calculatedValues = useMemo(() => {
      const entryPriceNum = parseFloat(entryPrice) || 0;
      const entryQuantityNum = parseFloat(entryQuantity) || 0;
      const entryFeesNum = parseFloat(entryFees) || 0;
      const exitPriceNum = parseFloat(exitPrice) || 0;
      const exitQuantityNum = parseFloat(exitQuantity) || 0;
      const exitFeesNum = parseFloat(exitFees) || 0;
      return calculateTradeValues({
        entryPrice: entryPriceNum,
        entryQuantity: entryQuantityNum,
        entryFees: entryFeesNum,
        exitPrice: status === "closed" ? exitPriceNum : undefined,
        exitQuantity: status === "closed" ? exitQuantityNum : undefined,
        exitFees: status === "closed" ? exitFeesNum : undefined,
        status,
        type,
      });
    }, [entryPrice, entryQuantity, entryFees, exitPrice, exitQuantity, exitFees, status, type]);

    const rr = useMemo(() => {
      const ep = parseFloat(entryPrice) || 0;
      const xp = parseFloat(exitPrice) || undefined;
      if (ep <= 0) return null;
      return calculateRiskRewardPreview({ entryPrice: ep, type, status, exitPrice: xp });
    }, [entryPrice, exitPrice, type, status]);

    const totalFees = useMemo(() => {
      const ef = parseFloat(entryFees) || 0;
      const xf = status === "closed" ? parseFloat(exitFees) || 0 : 0;
      return ef + xf;
    }, [entryFees, exitFees, status]);

    useEffect(() => {
      if (!initialData) return;
      if (initialData.symbol !== undefined) setSymbol(initialData.symbol);
      if (initialData.type !== undefined) setType(initialData.type);
      if (initialData.status !== undefined) setStatus(initialData.status);
      if (initialData.entryDate !== undefined) setEntryDate(initialData.entryDate);
      if (initialData.exitDate !== undefined) setExitDate(initialData.exitDate || "");
      if (initialData.entryPrice !== undefined) setEntryPrice(initialData.entryPrice.toString());
      if (initialData.entryQuantity !== undefined) setEntryQuantity(initialData.entryQuantity.toString());
      if (initialData.entryFees !== undefined) setEntryFees(initialData.entryFees.toString());
      if (initialData.exitPrice !== undefined) setExitPrice(initialData.exitPrice?.toString() || "");
      if (initialData.exitQuantity !== undefined) setExitQuantity(initialData.exitQuantity?.toString() || "");
      if (initialData.exitFees !== undefined) setExitFees(initialData.exitFees?.toString() || "0");
      if (initialData.preTradeEmotion !== undefined) setPreTradeEmotion(initialData.preTradeEmotion || "");
      if (initialData.duringTradeEmotion !== undefined)
        setDuringTradeEmotion(initialData.duringTradeEmotion || "");
      if (initialData.postTradeEmotion !== undefined) setPostTradeEmotion(initialData.postTradeEmotion || "");
      if (initialData.notes !== undefined) setJournalContent(initialData.notes || "");
      setErrors({});
    }, [initialData]);

    useEffect(() => {
      if (!attachmentStorageId) {
        setAttachments([]);
        return;
      }
      let cancelled = false;
      void getAttachments(attachmentStorageId).then((loaded) => {
        if (!cancelled) setAttachments(loaded);
      });
      return () => {
        cancelled = true;
      };
    }, [attachmentStorageId]);

    useEffect(() => {
      if (status === "open") {
        setExitDate("");
        setExitPrice("");
        setExitQuantity("");
        setExitFees("0");
      } else if (status === "closed" && !exitDate) {
        setExitDate(entryDate);
      }
    }, [status, entryDate, exitDate]);

    const formatPercent = (value: number): string =>
      `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;

    const buildFormData = (): TradeFormData => {
      const entryPriceNum = parseFloat(entryPrice) || 0;
      const entryQuantityNum = parseFloat(entryQuantity) || 0;
      const entryFeesNum = parseFloat(entryFees) || 0;
      const core: TradeFormData = {
        symbol: symbol.trim(),
        type,
        status,
        entryDate,
        entryPrice: entryPriceNum,
        entryQuantity: entryQuantityNum,
        entryFees: entryFeesNum,
        exitDate: status === "closed" ? exitDate : undefined,
        exitPrice: status === "closed" ? parseFloat(exitPrice) : undefined,
        exitQuantity: status === "closed" ? (exitQuantity ? parseFloat(exitQuantity) : undefined) : undefined,
        exitFees: status === "closed" ? parseFloat(exitFees) || 0 : undefined,
        notes: journalContent.trim() ? journalContent : undefined,
        preTradeEmotion: preTradeEmotion
          ? (preTradeEmotion as TradeFormData["preTradeEmotion"])
          : undefined,
        duringTradeEmotion: duringTradeEmotion
          ? (duringTradeEmotion as TradeFormData["duringTradeEmotion"])
          : undefined,
        postTradeEmotion: postTradeEmotion
          ? (postTradeEmotion as TradeFormData["postTradeEmotion"])
          : undefined,
      };
      return mergeExtendedFromInitial(core, initialData);
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      const formData = buildFormData();
      const validationErrors = validateTradeForm(formData);
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length === 0 && onSubmit) {
        onSubmit(formData);
      }
    };

    const currentAttachment = attachments.length > 0 ? attachments[0] : null;
    const imageUrl = currentAttachment ? getAttachmentDataURL(currentAttachment) : null;

    const handleUploadClick = () => {
      fileInputRef.current?.click();
    };

    const handleDeleteAttachment = (attachmentId: string) => {
      if (!attachmentStorageId) return;
      void deleteAttachment(attachmentStorageId, attachmentId)
        .then(() => {
          setAttachments([]);
          toast.success("Attachment removed", {
            description: "The screenshot has been removed.",
          });
        })
        .catch((error: unknown) => {
          presentMutationError(error, "Delete failed");
        });
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !attachmentStorageId) return;

      if (!file.type.startsWith("image/")) {
        toastError("Invalid file type", {
          description: "Please select an image file (PNG, JPG, etc.).",
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toastError("File too large", {
          description: "Please select an image smaller than 5MB.",
        });
        return;
      }

      if (!user?.id) {
        toastError("Authentication required", {
          description: "Please sign in to upload attachments.",
        });
        return;
      }

      setIsUploading(true);
      try {
        if (attachments.length > 0) {
          await Promise.all(
            attachments.map((att) => deleteAttachment(attachmentStorageId, att.id))
          );
        }

        const result = await uploadAttachment(file, attachmentStorageId, user.id);
        if (result.success && result.attachment) {
          setAttachments([result.attachment]);
          toast.success("Attachment uploaded", {
            description: `${file.name} has been uploaded successfully.`,
          });
        } else {
          toastError("Upload failed", {
            description: result.error || "Failed to upload attachment.",
          });
        }
      } catch (error) {
        console.error("Error uploading attachment:", error);
        toastError("Upload failed", {
          description: "An error occurred while uploading the attachment.",
        });
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    };

    return (
      <form ref={ref} onSubmit={handleSubmit}>
        <Card p={0} boxShadow="none">
          <CardBody w="100%" maxW="100%" overflowX="hidden" p={0}>
            <VStack spacing={6} align="stretch" w="100%" maxW="100%">
              {Object.keys(errors).length > 0 && (
                <Box borderRadius="md" borderWidth="1px" borderColor="red.300" bg="red.50" _dark={{ bg: "red.900", borderColor: "red.500" }} p={3}>
                  <Text fontSize="sm" color="red.600" _dark={{ color: "red.200" }} fontFamily="'IBM Plex Sans', sans-serif">
                    {errors.symbol ||
                      errors.entryDate ||
                      errors.entryPrice ||
                      errors.entryQuantity ||
                      errors.exitDate ||
                      errors.exitPrice ||
                      errors.exitQuantity ||
                      errors.general ||
                      "Please fix the highlighted fields."}
                  </Text>
                </Box>
              )}

              <Flex direction={{ base: "column", lg: "row" }} gap={{ base: 6, lg: 12 }} align="stretch" h="100%">
                {/* Left column: editable overview + financial fields (details modal uses a single-row summary table instead) */}
                <VStack spacing={6} align="stretch" flex="1" minW={0} h="100%">
                  <Box display="flex" flexDirection="column">
                    <Text fontSize="lg" fontWeight="normal" color="#38BBD1" mb={4} fontFamily="'Urbanist', sans-serif">
                      Trade Overview
                    </Text>
                    {/* Row 1: Symbol, Type, Status */}
                    <Flex justify="space-between" w="100%" pl={0} mb={4} gap={2} flexWrap="wrap">
                      <Box flex="1" minW="100px">
                        <Text {...labelProps}>Symbol</Text>
                        <Input
                          value={symbol}
                          onChange={(e) => setSymbol(e.target.value)}
                          placeholder="Symbol"
                          mt={0}
                          {...inputFieldProps}
                          borderColor={errors.symbol ? "red.400" : borderColor}
                        />
                      </Box>
                      <Box flex="1" minW="100px">
                        <Text {...labelProps}>Type</Text>
                        <Select
                          value={type}
                          onChange={(e) => setType(e.target.value as TradeType)}
                          {...inputFieldProps}
                        >
                          <option value="buy">Buy/Long</option>
                          <option value="sell">Sell/Short</option>
                        </Select>
                      </Box>
                      <Box flex="1" minW="100px">
                        <Text {...labelProps}>Status</Text>
                        <Select
                          value={status}
                          onChange={(e) => setStatus(e.target.value as TradeStatus)}
                          {...inputFieldProps}
                        >
                          <option value="open">Open</option>
                          <option value="closed">Closed</option>
                        </Select>
                      </Box>
                    </Flex>
                    {/* Row 2: Entry Date/Time, Exit Date/Time, Risk/Reward */}
                    <Flex justify="space-between" w="100%" pl={0} gap={2} flexWrap="wrap">
                      <Box flex="1" minW="120px">
                        <Text {...labelProps}>Entry Date/Time</Text>
                        <Input
                          type="datetime-local"
                          value={entryDate}
                          onChange={(e) => setEntryDate(e.target.value)}
                          {...inputFieldProps}
                          borderColor={errors.entryDate ? "red.400" : borderColor}
                        />
                      </Box>
                      {status === "closed" ? (
                        <Box flex="1" minW="120px">
                          <Text {...labelProps}>Exit Date/Time</Text>
                          <Input
                            type="datetime-local"
                            value={exitDate}
                            onChange={(e) => setExitDate(e.target.value)}
                            {...inputFieldProps}
                            borderColor={errors.exitDate ? "red.400" : borderColor}
                          />
                        </Box>
                      ) : (
                        <Box flex="1" minW="120px" />
                      )}
                      <Box flex="1" minW="100px">
                        <Text {...labelProps}>Risk/Reward</Text>
                        <Text fontSize="sm" color="gray.400" fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" mt={2}>
                          {rr !== null ? `${rr.toFixed(2)}:1` : "—"}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>

                  {/* Financial Summary — same structure as compact details */}
                  <Box display="flex" flexDirection="column">
                    <Text fontSize="lg" fontWeight="normal" color="#38BBD1" mb={4} fontFamily="'Urbanist', sans-serif">
                      Financial Summary
                    </Text>
                    {/* Row 1 — symbol, type, status, sync */}
                    <Flex justify="space-between" w="100%" pl={0} mb={4} gap={2} flexWrap="wrap">
                      <Box flex="1" minW="100px">
                        <Text {...labelProps}>Entry Price</Text>
                        <Input
                          type="number"
                          step="0.00001"
                          value={entryPrice}
                          onChange={(e) => setEntryPrice(e.target.value)}
                          {...inputFieldProps}
                          borderColor={errors.entryPrice ? "red.400" : borderColor}
                        />
                        <Text {...labelProps} mt={2} mb={1}>
                          Entry Fees
                        </Text>
                        <Input
                          type="number"
                          step="0.01"
                          value={entryFees}
                          onChange={(e) => setEntryFees(e.target.value)}
                          {...inputFieldProps}
                          size="xs"
                        />
                      </Box>
                      <Box flex="1" minW="100px">
                        <Text {...labelProps}>Lot Size</Text>
                        <Input
                          type="number"
                          step="0.00001"
                          value={entryQuantity}
                          onChange={(e) => setEntryQuantity(e.target.value)}
                          {...inputFieldProps}
                          borderColor={errors.entryQuantity ? "red.400" : borderColor}
                        />
                      </Box>
                      {status === "closed" ? (
                        <Box flex="1" minW="100px">
                          <Text {...labelProps}>Exit Price</Text>
                          <Input
                            type="number"
                            step="0.00001"
                            value={exitPrice}
                            onChange={(e) => setExitPrice(e.target.value)}
                            {...inputFieldProps}
                            borderColor={errors.exitPrice ? "red.400" : borderColor}
                          />
                          <Text {...labelProps} mt={2} mb={1}>
                            Exit Fees
                          </Text>
                          <Input
                            type="number"
                            step="0.01"
                            value={exitFees}
                            onChange={(e) => setExitFees(e.target.value)}
                            {...inputFieldProps}
                            size="xs"
                          />
                        </Box>
                      ) : (
                        <Box flex="1" minW="100px" />
                      )}
                      <Box flex="1" minW="100px">
                        <Text {...labelProps}>Total Fees</Text>
                        <Text fontSize="sm" color="gray.400" fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium">
                          {formatMoney(totalFees)}
                        </Text>
                      </Box>
                    </Flex>
                    {/* Row 2 — read-only preview matching details (computed) */}
                    <Flex justify="space-between" w="100%" pl={0} gap={2} flexWrap="wrap">
                      {calculatedValues.grossPnL !== undefined && (
                        <Box flex="1" minW="80px">
                          <Text {...labelProps}>Gross P&L</Text>
                          <Text
                            fontSize="sm"
                            color={calculatedValues.grossPnL >= 0 ? "#06E363" : "#E00101"}
                            fontFamily="'IBM Plex Sans', sans-serif"
                            fontWeight="medium"
                          >
                            {formatMoney(calculatedValues.grossPnL)}
                          </Text>
                        </Box>
                      )}
                      {calculatedValues.netPnL !== undefined && (
                        <Box flex="1" minW="80px">
                          <Text {...labelProps}>Net P&L</Text>
                          <Text
                            fontSize="sm"
                            color={calculatedValues.netPnL >= 0 ? "#06E363" : "#E00101"}
                            fontFamily="'IBM Plex Sans', sans-serif"
                            fontWeight="medium"
                          >
                            {formatMoney(calculatedValues.netPnL)}
                          </Text>
                        </Box>
                      )}
                      {calculatedValues.returnPercent !== undefined && (
                        <Box flex="1" minW="80px">
                          <Text {...labelProps}>Return %</Text>
                          <Text
                            fontSize="sm"
                            color={calculatedValues.returnPercent >= 0 ? "#06E363" : "#E00101"}
                            fontFamily="'IBM Plex Sans', sans-serif"
                            fontWeight="medium"
                          >
                            {formatPercent(calculatedValues.returnPercent)}
                          </Text>
                        </Box>
                      )}
                      {calculatedValues.roi !== undefined && (
                        <Box flex="1" minW="80px">
                          <Text {...labelProps}>ROI</Text>
                          <Text
                            fontSize="sm"
                            color={calculatedValues.roi >= 0 ? "#06E363" : "#E00101"}
                            fontFamily="'IBM Plex Sans', sans-serif"
                            fontWeight="medium"
                          >
                            {formatPercent(calculatedValues.roi)}
                          </Text>
                        </Box>
                      )}
                    </Flex>
                  </Box>
                </VStack>

                {/* Right column: market screenshot — same copy/behavior as trade details modal */}
                <Box flex="1" minW={0} display="flex" flexDirection="column" h="100%">
                  <Text fontSize="lg" fontWeight="normal" color="#38BBD1" mb={4} fontFamily="'Urbanist', sans-serif">
                    Market Screenshot
                  </Text>
                  <Text fontSize="xs" color="gray.400" mb={4} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" textTransform="uppercase">
                    Upload a screenshot of the market at the time you took this trade
                  </Text>
                  <Box
                    border="2px dashed"
                    borderColor={borderColor}
                    borderRadius="md"
                    p={imageUrl ? 0 : 8}
                    flex="1"
                    minH="200px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg={dashedAreaBg}
                    position="relative"
                    overflow="hidden"
                  >
                    {imageUrl ? (
                      <>
                        <Image
                          src={imageUrl}
                          alt={currentAttachment?.fileName || "Market screenshot"}
                          w="100%"
                          h="100%"
                          minH="200px"
                          objectFit="cover"
                          cursor="pointer"
                          onClick={onImageModalOpen}
                        />
                        <IconButton
                          aria-label="Remove screenshot"
                          icon={<AiOutlineDelete />}
                          size="sm"
                          position="absolute"
                          top={2}
                          right={2}
                          borderRadius="full"
                          bg="rgba(224, 1, 1, 0.8)"
                          color="white"
                          _hover={{ bg: "rgba(224, 1, 1, 1)" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (currentAttachment) {
                              handleDeleteAttachment(currentAttachment.id);
                            }
                          }}
                          zIndex={2}
                          isDisabled={isLoading}
                        />
                      </>
                    ) : (
                      <VStack spacing={2}>
                        <Button
                          size="md"
                          variant="outline"
                          leftIcon={<AiOutlineUpload />}
                          onClick={handleUploadClick}
                          isLoading={isUploading}
                          loadingText="Uploading..."
                          isDisabled={isLoading || !attachmentStorageId}
                          borderColor={borderColor}
                          fontFamily="'IBM Plex Sans', sans-serif"
                          fontSize="sm"
                          fontWeight="medium"
                          textTransform="uppercase"
                        >
                          Upload Screenshot
                        </Button>
                        <Text fontSize="xs" color="gray.400" fontFamily="'IBM Plex Sans', sans-serif">
                          PNG, JPG up to 5MB
                        </Text>
                      </VStack>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleFileSelect}
                    />
                  </Box>
                </Box>
              </Flex>

              {/* Journal: moods + notes — below overview columns */}
              <Box w="100%">
                <Text fontSize="lg" fontWeight="normal" color="#38BBD1" mb={4} fontFamily="'Urbanist', sans-serif">
                  Trade Journal
                </Text>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
                  <Box>
                    <Text {...journalLabelProps}>Pre-Trade Mood</Text>
                    <Select
                      value={preTradeEmotion}
                      onChange={(e) => setPreTradeEmotion(e.target.value)}
                      {...inputFieldProps}
                    >
                      <option value="">Select mood</option>
                      {PRE_TRADE_MOODS.map((mood) => (
                        <option key={mood.value} value={mood.value}>
                          {mood.label}
                        </option>
                      ))}
                    </Select>
                  </Box>
                  <Box>
                    <Text {...journalLabelProps}>During Trade Mood</Text>
                    <Select
                      value={duringTradeEmotion}
                      onChange={(e) => setDuringTradeEmotion(e.target.value)}
                      {...inputFieldProps}
                    >
                      <option value="">Select mood</option>
                      {DURING_TRADE_MOODS.map((mood) => (
                        <option key={mood.value} value={mood.value}>
                          {mood.label}
                        </option>
                      ))}
                    </Select>
                  </Box>
                  <Box>
                    <Text {...journalLabelProps}>Post-Trade Mood</Text>
                    <Select
                      value={postTradeEmotion}
                      onChange={(e) => setPostTradeEmotion(e.target.value)}
                      {...inputFieldProps}
                    >
                      <option value="">Select mood</option>
                      {POST_TRADE_MOODS.map((mood) => (
                        <option key={mood.value} value={mood.value}>
                          {mood.label}
                        </option>
                      ))}
                    </Select>
                  </Box>
                </SimpleGrid>

                <Text fontSize="xs" color="gray.400" mb={4} fontFamily="'IBM Plex Sans', sans-serif" fontWeight="medium" textTransform="uppercase">
                  Write about your trade experience, thoughts, and reflections
                </Text>
                <Box
                  bg={inputBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="md"
                  _focusWithin={{
                    borderColor: "#38BBD1",
                    boxShadow: "0 0 0 1px #38BBD1",
                  }}
                  sx={{
                    "& .quill": {
                      fontFamily: "'IBM Plex Sans', sans-serif",
                    },
                    "& .ql-container": {
                      fontSize: "sm",
                      color: textColor,
                      minHeight: "200px",
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      borderBottomLeftRadius: "md",
                      borderBottomRightRadius: "md",
                      borderColor,
                    },
                    "& .ql-editor": {
                      minHeight: "200px",
                      color: textColor,
                      "&.ql-blank::before": {
                        color: "gray.400",
                        fontStyle: "normal",
                        fontFamily: "'IBM Plex Sans', sans-serif",
                      },
                    },
                    "& .ql-toolbar": {
                      borderTopLeftRadius: "md",
                      borderTopRightRadius: "md",
                      borderColor,
                      bg: toolbarBg,
                    },
                    "& .ql-stroke": {
                      stroke: textColor,
                    },
                    "& .ql-fill": {
                      fill: textColor,
                    },
                    "& .ql-picker-label": {
                      color: textColor,
                    },
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={journalContent}
                    onChange={setJournalContent}
                    readOnly={isLoading}
                    placeholder="Document your trade experience, what you learned, what went well, what could be improved..."
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, 3, false] }],
                        ["bold", "italic", "underline", "strike"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ color: [] }, { background: [] }],
                        ["link"],
                        ["clean"],
                      ],
                    }}
                  />
                </Box>
              </Box>

              <Modal isOpen={isImageModalOpen} onClose={onImageModalClose} size="6xl" isCentered>
                <ModalOverlay bg="blackAlpha.700" />
                <ModalContent bg={modalBg} maxW="90vw" maxH="90vh" p={0}>
                  <ModalCloseButton
                    color="white"
                    bg="rgba(0, 0, 0, 0.6)"
                    _hover={{ bg: "rgba(0, 0, 0, 0.8)" }}
                    borderRadius="full"
                    zIndex={10}
                  />
                  <Box
                    w="100%"
                    h="100%"
                    maxH="90vh"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    p={4}
                    overflow="hidden"
                  >
                    <Image
                      src={imageUrl || ""}
                      alt={currentAttachment?.fileName || "Market screenshot"}
                      maxW="100%"
                      maxH="90vh"
                      objectFit="contain"
                    />
                  </Box>
                </ModalContent>
              </Modal>
            </VStack>
          </CardBody>
        </Card>
      </form>
    );
  }
);

TradeForm.displayName = "TradeForm";

export default TradeForm;
